import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ToastService } from '../toast-service';

export enum StatusCode {
  NotFound = 404,
  Unauthorized = 401,
  Forbidden = 403,
  InternalServerError = 500,
  BadRequest = 400
}

export interface CustomHttpOptions {
  headers?: HttpHeaders | { [header: string]: string | string[] };
  params?: any;
  observe?: any;
  responseType?: any;
  reportProgress?: boolean;
  withCredentials?: boolean;
  blockScreenOnError?: boolean;
  suppressGlobalErrorHandler?: boolean;
}

abstract class BaseAPIClient {
  protected abstract baseUrl: string;

  constructor(
    protected toastService: ToastService,
    protected http: HttpClient
  ) { }

  get<T>(endpoint: string, options?: CustomHttpOptions): Observable<T> {
    const url = this.buildUrl(endpoint);
    const { blockScreenOnError, suppressGlobalErrorHandler, ...httpOptions } = options || {};
    return this.http
      .get<T>(url, httpOptions)
      .pipe(catchError((error) => this.handleError(error, blockScreenOnError, suppressGlobalErrorHandler)));
  }

  post<T>(
    endpoint: string,
    body: any,
    options?: CustomHttpOptions
  ): Observable<T> {
    const url = this.buildUrl(endpoint);
    const { blockScreenOnError, suppressGlobalErrorHandler, ...httpOptions } = options || {};
    return this.http
      .post<T>(url, body, httpOptions)
      .pipe(catchError((error) => this.handleError(error, blockScreenOnError, suppressGlobalErrorHandler)));
  }

  put<T>(
    endpoint: string,
    body: any,
    options?: CustomHttpOptions
  ): Observable<T> {
    return this._put<T>(endpoint, body, options);
  }

  putWithoutBody<T>(
    endpoint: string,
    options?: CustomHttpOptions
  ): Observable<T> {
    return this._put<T>(endpoint, null, options);
  }

  private _put<T>(
    endpoint: string,
    body: any,
    options?: CustomHttpOptions
  ): Observable<T> {
    const url = this.buildUrl(endpoint);
    const { blockScreenOnError, suppressGlobalErrorHandler, ...httpOptions } = options || {};
    return this.http
      .put<T>(url, body, httpOptions)
      .pipe(catchError((error) => this.handleError(error, blockScreenOnError, suppressGlobalErrorHandler)));
  }

  delete<T>(endpoint: string, options?: CustomHttpOptions): Observable<T> {
    const url = this.buildUrl(endpoint);
    const { blockScreenOnError, suppressGlobalErrorHandler, ...httpOptions } = options || {};
    return this.http
      .delete<T>(url, httpOptions)
      .pipe(catchError((error) => this.handleError(error, blockScreenOnError, suppressGlobalErrorHandler)));
  }

  private buildUrl(endpoint: string): string {
    const normalizedBase = this.baseUrl.replace(/\/+$/, '');
    const normalizedEndpoint = endpoint.replace(/^\/+/, '');
    return `${normalizedBase}/${normalizedEndpoint}`;
  }

  private handleError(
    error: HttpErrorResponse,
    blockScreenOnError?: boolean,
    suppressGlobalErrorHandler?: boolean
  ): Observable<never> {
    if (error.status !== StatusCode.NotFound) {
    }
    return throwError(() => error);
  }
}

@Injectable({
  providedIn: 'root',
})
export class WebAPIClient extends BaseAPIClient {
  protected baseUrl: string = environment.webapi.baseUrl

  constructor(toastr: ToastService, http: HttpClient) {
    super(toastr, http);
  }
}

@Injectable({
  providedIn: 'root',
})
export class LMSAPIClient extends BaseAPIClient {
  protected baseUrl: string = environment.lmsapi.baseUrl

  constructor(toastr: ToastService, http: HttpClient) {
    super(toastr, http);
  }
}